import React, { useState } from 'react';
import { Table, Button, Icon, Tooltip, Modal, message, Tag } from 'antd';
import { Link } from 'react-router-dom';
import moment from 'moment';

import OTALogo from 'components/OTALogo/OTALogo';

import { useGetPaginatedReservations } from 'utils/apis/reservation';
import { getConversation, getBookingcomConversation, getCtripCMConversation, getAirbnbConversationByConfirmationCode } from 'utils/apis/conversation';
import { useFetchConstant } from 'hooks/constants';
import { cleanNumber, checkHasValue } from 'utils/general';
import {
  handleOnAggregationTableChange,
  constructColumn,
  constructColumnFilterSearch,
  constructColumnDateSearch,
  constructColumnFilterCheckbox
} from 'utils/table/table';
import { buildUnifiedInboxUrl } from 'utils/routes';

import CsvModal from './../components/CsvModal/CsvModal';
import PdfCardModal from 'components/ReceiptTemplate/PdfCardModal';

import './Reservation.css';
import intl from 'react-intl-universal';

import { MIN_INTL_CONTACT_NO_LEN, PREAUTH_HOST_IDS } from 'utils/constants';
import { withAppContext } from 'context/AppContext';
import ImportReservation from '../components/CsvModal/importExcelModal';

const COLLECT_COLOUR = { backgroundColor: '#f5222d' };
const REFUND_COLOUR = { backgroundColor: '#52c41a' };

const stripBracketFromCode = code =>
  code
    ? String(code)
        .replace(/\s*\(.*?\)\s*/g, '')
        .trim()
    : code;
const constructColumns = (bookingTypes, bookingStatuses, showChatButton, generateReport, { filters, sorter, dateFilter }) => {
  const getReceiptRenderProp = bookingTypes => ({
    render: (text, record) => {
      const bookingType = !!bookingTypes.find(type => type.code === text && type.hasPrice);
      if (bookingType) {
        return (
          <span>
            <Button type="primary" onClick={generateReport(record._id)}>
              <Icon type="download" />
            </Button>
          </span>
        );
      } else {
        return;
      }
    }
  });

  const getChatRenderProp = () => ({
    render: (text, record) => {
      const platform = record.platform;
      if (platform === 'bookingcom' || platform === 'airbnb' || platform === 'ctripcm') {
        return (
          <a
            href={buildUnifiedInboxUrl()}
            target="_blank"
            rel="noopener noreferrer"
            onClick={async e => {
              e.preventDefault();
              await getConversation(
                record.platform,
                record.platform === 'bookingcom' || record.platform === 'ctripcm' ? stripBracketFromCode(record.code) : record.sourceId
              ).then(res => {
                if (res && res.status === 200 && res.data) {
                  window.open(buildUnifiedInboxUrl(), '_blank', 'noopener,noreferrer');
                } else {
                  if (res && res.status === 404) {
                    if (record.platform === 'airbnb') {
                      Modal.confirm({
                        title: intl.get('reservations.io.errorMsg').d('No conversation found!'),
                        content: intl
                          .get('reservations.io.startConversation')
                          .d('There are no existing conversations found. You can initiate a conversation with the guest now. Proceed?'),
                        onOk() {
                          getAirbnbConversationByConfirmationCode(record.sourceId)
                            .then(bootstrapRes => {
                              if (!bootstrapRes || bootstrapRes.status !== 200) {
                                return Promise.reject(bootstrapRes);
                              }
                              return getConversation(record.platform, record.sourceId);
                            })
                            .then(() => {
                              window.open(buildUnifiedInboxUrl(), '_blank', 'noopener,noreferrer');
                            })
                            .catch(ex => {
                              console.log(ex);
                            });
                        },
                        onCancel() {}
                      });
                    } else if (record.platform === 'bookingcom') {
                      Modal.confirm({
                        title: intl.get('reservations.io.errorMsg').d('No conversation found!'),
                        content: intl
                          .get('reservations.io.startConversation')
                          .d('There are no existing conversations found. You can initiate a conversation with the guest now. Proceed?'),
                        onOk() {
                          // this.setState({ isLoading: true });
                          Promise.all([
                            getBookingcomConversation(record.code),
                            getConversation(record.platform, record.platform === 'bookingcom' ? stripBracketFromCode(record.code) : record.sourceId)
                          ])
                            .then(() => {
                              // this.setState({ isLoading: false });
                              window.open(buildUnifiedInboxUrl(), '_blank', 'noopener,noreferrer');
                              // window.location.reload();
                            })
                            .catch(ex => {
                              console.log(ex);
                            });
                        },
                        onCancel() {}
                      });
                    } else if (record.platform === 'ctripcm') {
                      Modal.confirm({
                        title: intl.get('reservations.io.errorMsg').d('No conversation found!'),
                        content: intl
                          .get('reservations.io.startConversation')
                          .d('There are no existing conversations found. You can initiate a conversation with the guest now. Proceed?'),
                        onOk() {
                          getCtripCMConversation(stripBracketFromCode(record.code))
                            .then(ctripRes => {
                              if (!ctripRes || ctripRes.status >= 400) {
                                const reason = ctripRes && ctripRes.data && (ctripRes.data.message || ctripRes.data.error);
                                message.error(
                                  reason
                                    ? `${intl.get('reservations.io.startConversationFail').d('Failed to start conversation with guest.')} ${reason}`
                                    : intl.get('reservations.io.startConversationFail').d('Failed to start conversation with guest.')
                                );
                                return;
                              }
                              return getConversation(
                                record.platform,
                                record.platform === 'ctripcm' ? stripBracketFromCode(record.code) : record.sourceId
                              ).then(() => {
                                window.open(buildUnifiedInboxUrl(), '_blank', 'noopener,noreferrer');
                              });
                            })
                            .catch(ex => {
                              const reason = ex && ex.data && (ex.data.message || ex.data.error);
                              message.error(
                                reason
                                  ? `${intl.get('reservations.io.startConversationFail').d('Failed to start conversation with guest.')} ${reason}`
                                  : intl.get('reservations.io.startConversationFail').d('Failed to start conversation with guest.')
                              );
                            });
                        },
                        onCancel() {}
                      });
                    }
                  }
                }
              });
            }}
          >
            <Tooltip title={<span style={{ fontSize: '12px' }}>{'Chat with guest'}</span>}>
              <Button type="primary" shape="circle">
                <Icon type="message" />
              </Button>
            </Tooltip>
          </a>
        );
      } else {
        return;
      }
    }
  });

  const getCodeRenderProp = () => ({
    render: (text, record) => (
      <Link to={`/reservation/${record._id}/edit`}>
        <OTALogo otaCode={record.platform} text={text} size="small" />
      </Link>
    )
  });

  const getContactNoRenderProp = () => ({
    render: text => {
      const contactNo = text || 'N/A';
      return contactNo && contactNo.length > MIN_INTL_CONTACT_NO_LEN ? (
        <Tooltip title={intl.get('reservations.headerLabels.chat').d('Chat on WhatsApp')}>
          <a href={`https://wa.me/${cleanNumber(contactNo)}`} target="_blank" rel="noopener noreferrer">
            {contactNo}
          </a>
        </Tooltip>
      ) : (
        <>{contactNo}</>
      );
    }
  });

  const getDepositRenderProp = () => {
    const DepositDisplay = ({ amount, isRefund = false, isCollect = false }) => {
      const title =
        (isRefund && intl.get('booking_form.paymentDetailsLabels.refunded').d('Refunded')) ||
        (isCollect && intl.get('booking_form.paymentDetailsLabels.collected').d('collected'));

      return (
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <Tooltip title={title}>
            <div
              style={{
                ...(isRefund && REFUND_COLOUR),
                ...(isCollect && COLLECT_COLOUR),
                borderRadius: '50%',
                width: 8,
                height: 8,
                display: 'inline-block'
              }}
            />
          </Tooltip>
          <span style={{ marginLeft: 4 }}>{amount}</span>
        </div>
      );
    };

    const PREAUTH_STATUS_CONFIG = {
      pending: { color: 'orange', label: 'Pending' },
      authorized: { color: 'blue', label: 'Hold Active' },
      captured: { color: 'red', label: 'Charged' },
      released: { color: 'green', label: 'Released' },
      expired: { color: '', label: 'Expired' },
      failed: { color: 'red', label: 'Failed' }
    };

    const getPreAuthAmountLabel = securityDeposit => {
      const { status, authorizedAmount, capturedAmount } = securityDeposit || {};
      if (status === 'captured' && !isNaN(capturedAmount)) return ` ${capturedAmount}`;
      if ((status === 'authorized' || status === 'released') && !isNaN(authorizedAmount)) return ` ${authorizedAmount}`;
      return '';
    };

    const getPreAuthAmountSummary = securityDeposit => {
      const { status, authorizedAmount, capturedAmount } = securityDeposit || {};
      if (status === 'captured' && !isNaN(capturedAmount) && !isNaN(authorizedAmount)) {
        const releasedAmount = (authorizedAmount - capturedAmount).toFixed(2);
        return `Captured ${capturedAmount} · Released ${releasedAmount} of ${authorizedAmount} hold`;
      }
      if (status === 'released' && !isNaN(authorizedAmount)) return `Released full ${authorizedAmount} hold`;
      if (status === 'authorized' && !isNaN(authorizedAmount)) return `${authorizedAmount} hold active on guest card`;
      return '';
    };

    return {
      render: (deposit, record) => {
        const { collect, refund } = deposit || {};
        const preAuthStatus = record.securityDeposit && record.securityDeposit.status;
        const hasDeposit = !isNaN(refund) || !isNaN(collect);

        if (!hasDeposit && !preAuthStatus) {
          return '-';
        }

        return (
          <div style={{ whiteSpace: 'nowrap' }}>
            {!isNaN(refund) ? (
              <DepositDisplay amount={refund} isRefund={true} />
            ) : !isNaN(collect) ? (
              <DepositDisplay amount={collect} isCollect={true} />
            ) : null}
            {preAuthStatus && PREAUTH_STATUS_CONFIG[preAuthStatus] && (
              <Tooltip title={getPreAuthAmountSummary(record.securityDeposit)}>
                <Tag color={PREAUTH_STATUS_CONFIG[preAuthStatus].color} style={{ marginTop: hasDeposit ? 4 : 0 }}>
                  {PREAUTH_STATUS_CONFIG[preAuthStatus].label}
                  {getPreAuthAmountLabel(record.securityDeposit)}
                </Tag>
              </Tooltip>
            )}
          </div>
        );
      }
    };
  };

  return [
    {
      ...constructColumn(intl.get('reservations.tableColumns.code').d('Confirmation Code'), 'code', {
        hasSorter: true,
        sorter,
        width: 150,
        fixed: 'left'
      }),
      ...constructColumnFilterSearch('code', intl.get('tables.confirmationCode').d('Search Confirmation Code'), filters),
      ...getCodeRenderProp()
    },
    {
      ...constructColumn(intl.get('reservations.tableColumns.property').d('Property Name'), 'propertyName', { hasSorter: true, sorter, width: 150 }),
      ...constructColumnFilterSearch('propertyName', intl.get('tables.property').d('Search Property Name'), filters)
    },
    {
      ...constructColumn(intl.get('reservations.tableColumns.unit').d('Unit Name'), 'unitName', { hasSorter: true, sorter, width: 150 }),
      ...constructColumnFilterSearch('unitName', intl.get('tables.unit').d('Search Unit Name'), filters)
    },
    {
      ...constructColumn(intl.get('reservations.tableColumns.checkIn').d('Check-in Date'), 'startDate', { hasSorter: true, sorter, width: 150 }),
      ...constructColumnDateSearch('startDate', intl.get('tables.startDate').d('Search Start Date'), dateFilter)
    },
    {
      ...constructColumn(intl.get('reservations.tableColumns.checkOut').d('Check-out Date'), 'endDate', { hasSorter: true, sorter, width: 150 }),
      ...constructColumnDateSearch('endDate', intl.get('tables.endDate').d('Search End Date'), dateFilter)
    },
    {
      ...constructColumn(intl.get('reservations.tableColumns.guest').d('Guest Name'), 'guestName', { hasSorter: true, sorter, width: 150 }),
      ...constructColumnFilterSearch('guestName', intl.get('tables.guestName').d('Search Guest Name'), filters)
    },
    {
      ...constructColumn(intl.get('reservations.tableColumns.contactNumber').d('Contact Number'), 'contactNumber', { width: 150 }),
      ...constructColumnFilterSearch('contactNumber', intl.get('tables.contactNo').d('Search Contact Number'), filters),
      ...getContactNoRenderProp()
    },
    {
      ...constructColumn(intl.get('reservations.tableColumns.receivedDate').d('Received Date'), 'createdAt', { hasSorter: true, sorter, width: 150 }),
      ...constructColumnDateSearch('createdAt', intl.get('tables.receivedDate').d('Search Received Date'), dateFilter)
    },
    {
      ...constructColumn(intl.get('reservations.tableColumns.bookingStatus').d('Booking Status'), 'bookingStatus', {
        hasSorter: true,
        sorter,
        width: 150
      }),
      ...constructColumnFilterCheckbox(
        'bookingStatus',
        bookingStatuses.filter(bookingStatusValue => bookingStatusValue.showInMCFilter).map(data => ({ ...data, value: data.code }))
      ),
      render: text => {
        if (!checkHasValue(text)) {
          return <span>-</span>;
        }
        const match = bookingStatuses.find(bs => bs.code === text);
        const label = match ? match.label : String(text);
        const translated = intl.get(`reservations.reservationDetails.bookingStatus.${label}`).d(label);
        return <span>{translated}</span>;
      }
    },
    showChatButton && {
      ...constructColumn(intl.get('reservations.tableColumns.chat').d('Chat'), 'code', { width: 100 }),
      ...getChatRenderProp()
    },
    {
      ...constructColumn(intl.get('reservations.tableColumns.deposit').d('Deposit'), 'deposit', { width: 130 }),
      ...getDepositRenderProp()
    },
    {
      ...constructColumn(intl.get('reservations.tableColumns.receipt').d('Receipt'), 'bookingType', { width: 80 }),
      ...getReceiptRenderProp(bookingTypes)
    }
  ].filter(Boolean);
};

const useFetchConstants = () => {
  const { selection: bookingStatuses, isLoading: isBookingStatusesLoading } = useFetchConstant('bookingStatus');
  const { selection: bookingTypes, isLoading: isBookingTypesLoading } = useFetchConstant('bookingTypes');
  const { selection: states, isLoading: isStatesLoading } = useFetchConstant('statesMY');
  const { selection: countries, isLoading: isCountriesLoading } = useFetchConstant('countries', { valueKey: 'iso2', labelKey: 'name' });

  const selections = { bookingStatuses, bookingTypes, states, countries };
  const isLoading = isBookingStatusesLoading || isBookingTypesLoading || isStatesLoading || isCountriesLoading;

  return { selections, isLoading };
};

const Standard = props => {
  const [query, setQuery] = useState({});
  const [tableQuery, setTableQuery] = useState({});
  const [isReceiptModalVisible, setIsReceiptModalVisible] = useState(false);
  const [isCSVModalVisible, setIsCSVModalVisible] = useState(false);
  const [isImportReservationModalVisible, setIsImportReservationModalVisible] = useState(false);
  const [selectedReservationId, setSelectedReservationId] = useState();
  const {
    selections: { bookingStatuses, bookingTypes, states, countries },
    isLoading: isConstantLoading
  } = useFetchConstants();
  const { paginatedData: reservations, total, isLoading: isReservationsLoading } = useGetPaginatedReservations(query);

  const { checkIsAdminReadOnly, checkIsAdmin, hasUnifiedInboxGuestChatAccess } = props;
  const showChatButton = hasUnifiedInboxGuestChatAccess();
  const isLoading = isConstantLoading || isReservationsLoading;

  const generateReport = reservationId => e => {
    setSelectedReservationId(reservationId);
    setIsReceiptModalVisible(true);
  };

  const handleClose = () => {
    setSelectedReservationId();
    setIsReceiptModalVisible(false);
  };

  const onTableDataChange = newQuery => {
    setTableQuery(newQuery);
    handleOnAggregationTableChange(newQuery, setQuery);
  };

  const handleOnClickClearAll = () => {
    const newQuery = {
      pagination: { current: 1, pageSize: 10 },
      filters: {},
      sorter: {}
    };
    onTableDataChange(newQuery);
  };

  return (
    <>
      <div className="sp-controls">
        <div>
          <Link to={'/bookingForm'}>
            <Button id="create-button2-res" type="primary" icon="plus" disabled={checkIsAdminReadOnly()} style={{ marginRight: 8, marginBottom: 8 }}>
              {intl.get('reservations.headerLabels.createReservation').d('Create Reservation')}
            </Button>
          </Link>

          <Button
            id="create-button2-imp"
            type="default"
            icon="upload"
            disabled={checkIsAdminReadOnly()}
            onClick={() => setIsImportReservationModalVisible(true)}
          >
            {intl.get('reservations.headerLabels.importReservation').d('Import Reservation')}
          </Button>
        </div>
        <div>
          <Button onClick={handleOnClickClearAll} style={{ marginRight: 5 }}>
            {intl.get('reservations.headerLabels.reset').d('Reset All')}
          </Button>

          {props.checkAbleExportReservation() && (
            <Button
              id="csv-button1-res"
              icon="download"
              type="primary"
              onClick={() => setIsCSVModalVisible(true)}
              disabled={checkIsAdminReadOnly()}
              // disabled={!props.checkIsAdmin()}
            >
              {intl.get('reservations.headerLabels.csv').d('Download CSV')}
            </Button>
          )}
        </div>
      </div>
      <Table
        rowKey={record => record._id}
        columns={constructColumns(bookingTypes, bookingStatuses, showChatButton, generateReport, tableQuery)}
        dataSource={reservations}
        scroll={{ x: 1500 }}
        loading={isLoading}
        onChange={(pagination, filters, sorter) => onTableDataChange({ pagination, filters, sorter })}
        pagination={{ total }}
      />
      {selectedReservationId && (
        <PdfCardModal
          reservationId={selectedReservationId}
          visible={isReceiptModalVisible}
          handleClose={handleClose}
          checkIsAdmin={props.checkIsAdmin}
        />
      )}
      <CsvModal
        visible={isCSVModalVisible}
        closeModal={() => setIsCSVModalVisible(false)}
        bookingStatus={bookingStatuses}
        bookingTypes={bookingTypes}
        countries={countries}
        states={states}
        isAdmin={props.checkIsAdmin()}
        isAllowViewTransaction={props.checkIsAllowViewTransaction()}
        showPreAuthColumns={props.checkIsAdmin() || (props.getUserHosts ? props.getUserHosts() : []).some(h => PREAUTH_HOST_IDS.includes(h))}
      />
      <ImportReservation visible={isImportReservationModalVisible} onCancel={() => setIsImportReservationModalVisible(false)} />
    </>
  );
};

export default withAppContext(Standard);
